import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, App, Button, Card, Col, Form, Input, InputNumber, Row, Select, Skeleton, Space, Switch, Tag, Typography } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage, fileUrl } from '../../api/client';
import { useBrandImage, useSettings, useUpdateSettings, type BrandImage, type Settings } from '../../features/settings/api';
import { LoadError } from '../../components/states';
import { CardTitle } from '../../components/StatCard';
import { IMAGE_ACCEPT } from '../../components/PhotoAvatar';
import { SidebarBrand } from '../../app/SidebarBrand';
import { palette } from '../../app/theme';
import { AiModelSelect } from '../../components/AiModelSelect';
import { useAiStatus, useSaveAiSettings } from '../../features/ai/api';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CHF', 'JPY', 'AED', 'INR'];

export function SettingsPage() {
  const { message } = App.useApp();
  const settings = useSettings();
  const update = useUpdateSettings();
  const [form] = Form.useForm<Settings>();
  // live preview of the sidebar header while typing
  const previewName = Form.useWatch('companyName', form);
  const previewTitle = Form.useWatch('brandTitle', form);
  const previewSubtitle = Form.useWatch('brandSubtitle', form);

  const onFinish = async (values: Settings) => {
    try {
      // the form stays mounted (an image upload must not wipe unsaved edits), so take the saved, trimmed values back
      form.setFieldsValue(await update.mutateAsync(values));
      message.success('Settings saved');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page page-narrow">
      <PageHeader title="General" subtitle="Workspace-wide defaults for names, branding, money, dates, duplicates and verification." />
      {settings.isError ? (
        <Card>
          <LoadError error={settings.error} onRetry={settings.refetch} title="Could not load settings" />
        </Card>
      ) : !settings.data ? (
        <Card>
          <Skeleton active />
        </Card>
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false} initialValues={settings.data}>
          <Space orientation="vertical" size={24} style={{ width: '100%' }}>
            <Card title={<CardTitle title="Workspace" subtitle="Name, money and date display used across the app" />}>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item name="companyName" label="Company name" rules={[{ required: true }]} extra="Shown in the sidebar and the browser tab.">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="defaultCurrency" label="Default currency">
                    <Select options={CURRENCIES.map((c) => ({ value: c, label: c }))} showSearch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="timezone" label="Time zone" extra="IANA name, e.g. Europe/London">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="dateFormat" label="Date format" extra="Used for dates across the app.">
                    <Select options={['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'D MMM YYYY'].map((f) => ({ value: f, label: f }))} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title={<CardTitle title="Branding" subtitle="The top-left corner of the sidebar and the browser tab icon" />}>
              <div style={{ width: 256, maxWidth: '100%', border: `1px solid ${palette.border}`, borderRadius: 12, background: palette.surface, marginBottom: 24 }} aria-label="Sidebar header preview">
                <SidebarBrand companyName={previewName} title={previewTitle} subtitle={previewSubtitle} logoFileId={settings.data.brandLogoFileId} />
              </div>
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <BrandImageField
                    kind="logo"
                    label="Logo"
                    fileId={settings.data.brandLogoFileId}
                    extra="Replaces the header text. A wide image works best; it is shown up to 40 px tall."
                    preview={(src) => <img src={src} alt="Current logo" style={{ maxWidth: 160, maxHeight: 40, objectFit: 'contain', display: 'block' }} />}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <BrandImageField
                    kind="favicon"
                    label="Browser tab icon"
                    fileId={settings.data.brandFaviconFileId}
                    extra="A square image. Without one, the tab shows the header's initial."
                    preview={(src) => <img src={src} alt="Current tab icon" style={{ width: 32, height: 32, objectFit: 'contain', display: 'block' }} />}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="brandTitle" label="Header text" rules={[{ max: 60 }]} extra="Shown when there is no logo. Leave empty to use the company name.">
                    <Input placeholder={previewName || 'Company name'} maxLength={60} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="brandSubtitle" label="Subtitle" rules={[{ max: 80 }]} extra="Optional small grey line under the logo or text.">
                    <Input placeholder="e.g. Sales workspace" maxLength={80} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title={<CardTitle title="Duplicates" subtitle="How contacts with matching details are merged or queued for review" />}>
              <Form.Item name={['dedupe', 'autoMergeExactEmail']} label="Auto-merge contacts with the same email" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name={['dedupe', 'foldGmailDots']} label="Treat gmail dots and +tags as the same address" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name={['dedupe', 'fuzzyNameThreshold']} label="Fuzzy name similarity threshold (0.5 – 1)" style={{ marginBottom: 0 }}>
                <InputNumber min={0.5} max={1} step={0.05} />
              </Form.Item>
            </Card>

            <Card title={<CardTitle title="Email verification" subtitle="Checks that protect your sending reputation" />}>
              <Form.Item name={['verification', 'builtinOnCreate']} label="Run built-in checks (syntax, MX, disposable) when an email is added" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name={['verification', 'requireVerifiedBeforeCampaign']} label="Require verified emails before campaign sends by default" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </Card>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" loading={update.isPending}>
                Save settings
              </Button>
            </div>
          </Space>
        </Form>
      )}
      <AiSettingsCard />
    </div>
  );
}

/** Which AI providers are connected and the model the "Write with AI" and "Revise with AI" buttons use by default. */
function AiSettingsCard() {
  const { message } = App.useApp();
  const status = useAiStatus();
  const save = useSaveAiSettings();
  const [model, setModel] = useState<string | null>(null);
  useEffect(() => setModel(status.data?.defaultModel ?? null), [status.data?.defaultModel]);
  const anyConfigured = status.data?.providers.some((p) => p.configured);

  return (
    <Card style={{ marginTop: 24 }} title={<CardTitle title="AI writing" subtitle="Claude and OpenAI models for writing and revising campaign emails" />}>
      {status.isError ? (
        <LoadError error={status.error} onRetry={status.refetch} title="Could not load AI settings" />
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {(status.data?.providers ?? []).map((p) => (
            <div key={p.provider} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Typography.Text strong style={{ minWidth: 150 }}>{p.label}</Typography.Text>
              {p.configured ? (p.error ? <Tag color="error">Key set, but: {p.error}</Tag> : <Tag color="success">Connected</Tag>) : <Tag>Not set up</Tag>}
              {!p.configured && <Typography.Text type="secondary" style={{ fontSize: 13 }}>Add <Typography.Text code>{p.envVar}</Typography.Text> to the server's environment (Render → Environment) and redeploy.</Typography.Text>}
            </div>
          ))}
          {status.isLoading && <Skeleton active paragraph={{ rows: 2 }} />}
          {status.data && !anyConfigured && <Alert type="info" showIcon title="Add an API key for Claude, OpenAI or both to turn on AI writing." />}
          {anyConfigured && (
            <div>
              <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Default model</Typography.Text>
              <Space.Compact style={{ width: '100%', maxWidth: 520 }}>
                <AiModelSelect status={status.data} value={model} onChange={setModel} />
                <Button
                  type="primary"
                  loading={save.isPending}
                  disabled={!model || model === status.data?.defaultModel}
                  onClick={() => save.mutateAsync({ defaultModel: model }).then(() => message.success('Default AI model saved')).catch((e) => message.error(errorMessage(e)))}
                >
                  Save
                </Button>
              </Space.Compact>
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>Anyone writing an email can still pick another model for one request.</Typography.Text>
            </div>
          )}
        </Space>
      )}
    </Card>
  );
}

/** Upload / replace / remove one branding image; saved right away, apart from the form's Save button. */
function BrandImageField({ kind, label, fileId, extra, preview }: { kind: BrandImage; label: string; fileId: string | null; extra: string; preview: (src: string) => ReactNode }) {
  const { message } = App.useApp();
  const mutation = useBrandImage(kind);
  const input = useRef<HTMLInputElement>(null);
  const run = async (file: File | null) => {
    try {
      await mutation.mutateAsync(file);
      message.success(file ? `${label} updated` : `${label} removed`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  const src = fileUrl(fileId);
  return (
    <Form.Item label={label} extra={extra}>
      <input
        ref={input}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: 'none' }}
        aria-label={`${label} file`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void run(file);
        }}
      />
      <Space size={12} align="center" wrap>
        {src ? (
          <div style={{ padding: 8, border: `1px solid ${palette.borderStrong}`, borderRadius: 8, background: palette.surface }}>{preview(src)}</div>
        ) : (
          <Typography.Text type="secondary">None</Typography.Text>
        )}
        <Button icon={<UploadOutlined />} loading={mutation.isPending && mutation.variables !== null} onClick={() => input.current?.click()}>
          {src ? 'Replace' : 'Upload'}
        </Button>
        {src && (
          <Button danger icon={<DeleteOutlined />} loading={mutation.isPending && mutation.variables === null} onClick={() => void run(null)}>
            Remove
          </Button>
        )}
      </Space>
    </Form.Item>
  );
}
